/**
 * HC-EPIC-004 · Sprint 1-B · Community Partner Portal API client.
 *
 * Design notes (owner directive §7):
 *   - Two distinct axios instances so a Community Partner JWT can never
 *     "spill over" onto staff pages and vice-versa.
 *   - Portal token lives in `sessionStorage` (per-tab, cleared on close)
 *     rather than `localStorage` so an accidentally-shared browser does
 *     not leak the portal session.
 *   - The short-lived `activation_session_token` is kept in memory only
 *     (React state). It is never written to storage.
 *   - Every method surfaces the raw axios error so pages can show generic
 *     copy for 401/403/404/409/422/429 without leaking backend `detail`
 *     strings when the payload might carry sensitive material.
 */
import axios from "axios";
import { API, api as staffApi } from "@/lib/api";

// ---------------------------------------------------------------------------
// Portal token (session-scoped, per-tab).
// ---------------------------------------------------------------------------
const CP_TOKEN_KEY = "hw_cp_portal_token";

export const getCommunityPartnerToken = () => {
  try { return sessionStorage.getItem(CP_TOKEN_KEY); } catch { return null; }
};
export const setCommunityPartnerToken = (t) => {
  try {
    if (t) sessionStorage.setItem(CP_TOKEN_KEY, t);
    else sessionStorage.removeItem(CP_TOKEN_KEY);
  } catch { /* storage unavailable */ }
};
export const clearCommunityPartnerToken = () => setCommunityPartnerToken(null);

/**
 * Portal-scoped axios instance. Only the Community Partner Portal token
 * is attached — never the staff bearer token.
 */
export const communityPartnerApi = axios.create({
  baseURL: API,
  withCredentials: false,
});
communityPartnerApi.interceptors.request.use((config) => {
  const t = getCommunityPartnerToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});
communityPartnerApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && typeof window !== "undefined") {
      clearCommunityPartnerToken();
    }
    return Promise.reject(err);
  }
);

/**
 * Anonymous / public activation client — no auth header.
 * Every call is one-shot; no interceptors.
 */
export const publicActivationApi = axios.create({
  baseURL: API,
  withCredentials: false,
});

// ---------------------------------------------------------------------------
// Public activation endpoints (anonymous, token-scoped).
// ---------------------------------------------------------------------------

export const lookupActivationToken = async (token) => {
  const { data } = await publicActivationApi.get(
    `/public/community-partner-activate/token/${encodeURIComponent(token)}`
  );
  return data; // { valid, organization_display_name?, role_label?, reason? }
};

/** Bind password, provision MFA. Returns activation_session_token +
 *  provisioning URI + QR PNG data URL. Caller must keep the session
 *  token in memory only (never storage). */
export const activateBindPasswordAndProvisionMfa = async (token, password) => {
  const { data } = await publicActivationApi.post(
    `/public/community-partner-activate/token/${encodeURIComponent(token)}`,
    { token, password }
  );
  return data;
};

/** Verify TOTP; returns the first portal bearer token. */
export const activateVerifyMfa = async (activationSessionToken, mfaCode) => {
  const { data } = await publicActivationApi.post(
    `/public/community-partner-activate/mfa-enroll`,
    {
      activation_session_token: activationSessionToken,
      mfa_code: mfaCode,
    }
  );
  return data; // { ok, access_token }
};

// ---------------------------------------------------------------------------
// Portal endpoints (community_partner_organization_id derived server-side).
// ---------------------------------------------------------------------------

export const portalListUsers = async () => {
  const { data } = await communityPartnerApi.get(`/community-partner-portal/users`);
  return data; // { items, count }
};

export const portalListInvitations = async () => {
  const { data } = await communityPartnerApi.get(`/community-partner-portal/invitations`);
  return data;
};

export const portalCreateInvitation = async ({ email, roleKey, displayName }) => {
  const { data } = await communityPartnerApi.post(`/community-partner-portal/invitations`, {
    email, role_key: roleKey, display_name: displayName || undefined,
  });
  return data;
};

export const portalRevokeInvitation = async (invitationId) => {
  const { data } = await communityPartnerApi.post(
    `/community-partner-portal/invitations/${invitationId}/revoke`
  );
  return data;
};

export const portalSuspendUser = async (userId) => {
  const { data } = await communityPartnerApi.post(
    `/community-partner-portal/users/${userId}/suspend`
  );
  return data;
};

// ---------------------------------------------------------------------------
// Staff-scoped endpoints (HarborWell admin). Uses the existing staff
// `api` instance so the same JWT / session-revocation stack applies.
// ---------------------------------------------------------------------------

export const adminListOrganizations = async () => {
  const { data } = await staffApi.get(`/admin/community-partner-organizations`);
  return data;
};

export const adminGetOrganization = async (orgId) => {
  const { data } = await staffApi.get(`/admin/community-partner-organizations/${orgId}`);
  return data;
};

export const adminCreateOrganization = async ({
  name, displayName, contactEmail, userLimit, requireApproval,
}) => {
  const { data } = await staffApi.post(`/admin/community-partner-organizations`, {
    name,
    display_name: displayName || undefined,
    contact_email: contactEmail || undefined,
    user_limit: userLimit ?? undefined,
    require_harborwell_approval_for_all_invitations: !!requireApproval,
  });
  return data;
};

export const adminTransitionOrgStatus = async (orgId, status, reason) => {
  const { data } = await staffApi.patch(
    `/admin/community-partner-organizations/${orgId}/status`,
    { status, reason: reason || undefined }
  );
  return data;
};

export const adminUpdateUserLimit = async (orgId, userLimit, reason) => {
  const { data } = await staffApi.patch(
    `/admin/community-partner-organizations/${orgId}/user-limit`,
    { user_limit: userLimit, reason: reason || undefined }
  );
  return data;
};

export const adminDesignateInitialAdmin = async (orgId, { email, displayName }) => {
  const { data } = await staffApi.post(
    `/admin/community-partner-organizations/${orgId}/initial-admin`,
    { email, display_name: displayName || undefined }
  );
  return data; // { invitation_id, activation_token, expires_at }
};

export const adminListUsers = async (organizationId) => {
  const url = organizationId
    ? `/admin/community-partner-users?organization_id=${encodeURIComponent(organizationId)}`
    : `/admin/community-partner-users`;
  const { data } = await staffApi.get(url);
  return data;
};

const adminUserTransition = (verb) => async (userId, reason) => {
  const { data } = await staffApi.post(
    `/admin/community-partner-users/${userId}/${verb}`,
    { reason: reason || undefined }
  );
  return data;
};

export const adminSuspendUser = adminUserTransition("suspend");
export const adminDeactivateUser = adminUserTransition("deactivate");
export const adminBlockUser = adminUserTransition("block");
export const adminReactivateUser = adminUserTransition("reactivate");

// ---------------------------------------------------------------------------
// Client-side helpers.
// ---------------------------------------------------------------------------

/**
 * Map a backend error into a generic, non-leaky user-facing string.
 * Never surfaces backend `detail` text verbatim for 4xx security-sensitive
 * codes (401/403/404) — the backend has been careful there but we double-
 * layer the redaction on the client too.
 */
export const formatCommunityPartnerError = (err, fallback = "Something went wrong") => {
  const status = err?.response?.status;
  if (!status) return fallback;
  if (status === 401) return "You are not signed in.";
  if (status === 403) return "You do not have permission to perform this action.";
  if (status === 404) return "The requested item was not found.";
  if (status === 409) {
    // 409 is used for state-transition denials — safe to surface backend
    // copy because the backend already keeps it generic.
    return err?.response?.data?.detail || "That action is not currently allowed.";
  }
  if (status === 422) return "The information you entered is invalid.";
  if (status === 429) return "Too many requests. Please try again in a few minutes.";
  if (status >= 500) return "The service is temporarily unavailable.";
  return fallback;
};

/** The subset of role keys an org administrator may assign via portal. */
export const ORG_ADMIN_ASSIGNABLE_ROLES = [
  { key: "community_partner_user", label: "Community Partner User" },
  { key: "community_partner_readonly", label: "Community Partner Read-Only User" },
];
